import type { PeriodData } from './order-aggregate.type'
import type { MaguchiAgentProposalType } from './iris-agent.type'
import type { SalesrepHeadsupSource } from './salesrep-headsup.type'

// AI 利用量 (user 起点: 実行した User の salesrep 番号に紐付く)
export type SalesrepActivityUsageMetrics = {
  dashboardChatMessages: number
  dashboardVoiceSessions: number
  // 通話時間合計 (秒)。終了時刻は最後に観測できた対話活動の近似で、
  // 終了未観測のセッション (対話中) は加算されない
  dashboardVoiceDurationSeconds: number
  irisUserMessages: number
}

export type SalesrepActivityUsageWeek = SalesrepActivityUsageMetrics & {
  periodIndex: number // 対応する weeks[].periodIndex
}

export type SalesrepActivityUsage = {
  total: SalesrepActivityUsageMetrics // 暦月の月計
  weeks: SalesrepActivityUsageWeek[]
}

// 顧客上の記録の動き (customer 起点。誰が操作したかは区別しない)
export type SalesrepActivityRecordsMetrics = {
  tasksCreated: number
  // report が書かれている task 数 (完了は問わない)。記入時刻は記録されないため
  // task の最終更新日時で期間に割り当てる
  taskReportsWritten: number
  headsupsCreated: number
  headsupsCreatedBySource: Record<SalesrepHeadsupSource, number>
  freeNotesCreated: number
  insightsCreated: number
  changesByUi: number // sales-matrix-row と customer-item-preference の変更の合算
  changesByAiProposal: number // 同上 (Iris 提案の承諾による変更)
  priorityChanges: number
}

export type SalesrepActivityRecordsWeek = SalesrepActivityRecordsMetrics & {
  periodIndex: number
}

export type SalesrepActivityRecords = {
  total: SalesrepActivityRecordsMetrics
  weeks: SalesrepActivityRecordsWeek[]
}

export type SalesrepActivityActiveDaysWeek = {
  periodIndex: number // 対応する weeks[].periodIndex
  activeDays: number
}

// 活動があった日数。usage (chat / voice / Iris) または記録活動の一部
// (task 作成・free note 作成・設定変更) が 1 件でもあった日を数える。
// 記録活動は customer 起点で実行者を持たないため、本人以外の操作も含む近似値
export type SalesrepActivityActiveDays = {
  total: number // 暦月内の活動日数
  weeks: SalesrepActivityActiveDaysWeek[]
}

// targetMonth に作成された提案の cohort。現在の status で数えるため、
// 月をまたいで承諾された分もこの月の accepted に入る
export type SalesrepActivityProposalFunnel = {
  type: MaguchiAgentProposalType
  proposed: number
  accepted: number
  pending: number
  outdated: number
}

export type SalesrepActivityItem = {
  salesrepNumber: number
  salesrepName: string
  usage: SalesrepActivityUsage
  records: SalesrepActivityRecords
  activeDays: SalesrepActivityActiveDays
  proposalFunnel: SalesrepActivityProposalFunnel[]
}

export type SalesrepActivityResponse = {
  targetMonth: string // YYYY-MM
  // 対象月と交差する完全週 (月曜始まり、古い順)。端の週は隣月の日を含むため
  // weeks の合計は total と一致しないことがある
  weeks: PeriodData[]
  items: SalesrepActivityItem[]
  notFoundSalesrepNumbers: number[]
}

// --- URL クエリパラメータの相互変換 ---

export type SalesrepActivityFilters = {
  salesrepNumbers: number[]
  month: string // YYYY-MM
}

export type SalesrepActivityRouteQuery = {
  salesrep_numbers?: string
  month?: string
}

export const parseSalesrepActivityQuery = (
  params: Record<string, unknown>,
): SalesrepActivityFilters => {
  const now = new Date()
  return {
    salesrepNumbers: params.salesrep_numbers
      ? [
          ...new Set(
            String(params.salesrep_numbers)
              .split(',')
              .map(Number)
              .filter((n) => Number.isInteger(n)),
          ),
        ]
      : [],
    month: params.month
      ? String(params.month)
      : `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
  }
}

export const buildSalesrepActivityQuery = (
  filters: SalesrepActivityFilters,
): SalesrepActivityRouteQuery => {
  const result: SalesrepActivityRouteQuery = {}
  if (filters.salesrepNumbers.length > 0) {
    result.salesrep_numbers = filters.salesrepNumbers.join(',')
  }
  if (filters.month) result.month = filters.month
  return result
}
